import { useTranslation } from 'react-i18next';

import { Image } from '@codes/ui-kit/components/ui/image';
import { WorkspaceView } from '@lemoncloud/codes-goods-api';
import { useWorkspaceService } from '@web/app/features/home/hooks';

import { WorkspaceImage } from '@codes/assets';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, Label } from '@codes/ui-kit';
import { formatDate } from '@codes/utils';

import { useDialogStore } from '../../stores';
import { getStereoDisplayName } from '../../utils';

interface ConfirmWorkspaceDialogProps {
    workspace?: WorkspaceView | null;
}
export const ConfirmWorkspaceDialog = ({ workspace }: ConfirmWorkspaceDialogProps) => {
    const { t } = useTranslation();
    const { isWorkspaceConfirmDialogOpen, setWorkspaceConfirmDialogOpen, closeWorkspaceConfirmDialog } =
        useDialogStore();

    const { replaceWorkspace } = useWorkspaceService();

    const onClose = async () => {
        if (!workspace) {
            closeWorkspaceConfirmDialog();
            return;
        }

        await replaceWorkspace(workspace.id as string, workspace.siteId as string);
        closeWorkspaceConfirmDialog();
    };

    return (
        <Dialog open={isWorkspaceConfirmDialogOpen} onOpenChange={setWorkspaceConfirmDialogOpen}>
            <DialogContent className="py-0">
                <DialogHeader></DialogHeader>
                <div className="pt-5 px-6 pb-10 overflow-auto">
                    {!workspace ? (
                        <div className="flex flex-col justify-center items-center gap-4 py-10">
                            <div className="text-lg text-red-600 font-medium text-center">
                                {t('confirmWorkspaceDialog.invalidAccess')}
                            </div>
                            <div className="text-sub text-center">{t('confirmWorkspaceDialog.workspaceNotFound')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col justify-center items-center gap-4">
                                <Image className="w-20" src={WorkspaceImage} />
                                <div className="text-lg text-text font-medium text-center">
                                    {t('confirmWorkspaceDialog.subscriptionCompleted', {
                                        stereo: getStereoDisplayName(workspace.stereo),
                                    })}
                                </div>
                                <div className="text-sub text-center font-medium whitespace-pre">
                                    {t('confirmWorkspaceDialog.emailSentMessage')}
                                </div>
                            </div>
                            <div className="mt-8">
                                <Label variant="form" className="text-base">
                                    {t('confirmWorkspaceDialog.subscriptionInfo')}
                                </Label>
                                <div className="border border-gradient rounded-xl overflow-hidden ">
                                    <div className="py-3 px-[19px] flex flex-col divide-y">
                                        <div className="py-3 flex items-center justify-between">
                                            <div className="text-sm text-sub">
                                                {t('confirmWorkspaceDialog.subscriptionHistory')}
                                            </div>
                                            <div className="text-text font-semibold">{workspace.name}</div>
                                        </div>
                                        <div className="py-3 flex items-center justify-between">
                                            <div className="text-sm text-sub">
                                                {t('confirmWorkspaceDialog.subscriptionDate')}
                                            </div>
                                            <div className="text-text font-semibold">
                                                {formatDate(workspace.approvedAt || workspace.createdAt)}
                                            </div>
                                        </div>
                                        <div className="py-3 flex items-center justify-between">
                                            <div className="text-sm text-sub">{t('confirmWorkspaceDialog.cloud')}</div>
                                            <div className="text-text font-semibold">{workspace.cloud$?.name}</div>
                                        </div>
                                        <div className="py-3 flex items-center justify-between">
                                            <div className="text-sm text-sub">
                                                {t('confirmWorkspaceDialog.expectedPaymentAmount')}
                                            </div>
                                            <div className="text-text font-bold text-lg">-</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter className="px-6">
                    <Button size="lg" className="w-full" onClick={onClose}>
                        {t('common.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
